import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

type Status = 'connected' | 'connecting' | 'disconnected' | 'error';

interface ConnectionStatusProps {
  status: Status;
  onReconnect?: () => void;
  canRetry?: boolean;
  className?: string;
  variant?: 'badge' | 'inline' | 'banner';
}

const statusConfig: Record<Status, { color: string; bgColor: string; icon: typeof Wifi }> = {
  connected: {
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    icon: Wifi,
  },
  connecting: {
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    icon: RefreshCw,
  },
  disconnected: {
    color: 'text-secondary-500 dark:text-secondary-400',
    bgColor: 'bg-secondary-100 dark:bg-secondary-700',
    icon: WifiOff,
  },
  error: {
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    icon: AlertCircle,
  },
};

function ConnectionStatusComponent({
  status,
  onReconnect,
  canRetry = true,
  className,
  variant = 'badge',
}: ConnectionStatusProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const config = statusConfig[status];
  const Icon = config.icon;

  if (variant === 'badge') {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
          config.bgColor,
          config.color,
          className
        )}
        role="status"
        aria-live="polite"
      >
        <Icon
          className={cn('h-3.5 w-3.5', status === 'connecting' && 'animate-spin')}
          aria-hidden="true"
        />
        <span>{t(`realtime.${status}`)}</span>
        {(status === 'disconnected' || status === 'error') && canRetry && onReconnect && (
          <button
            type="button"
            onClick={onReconnect}
            className="ltr:ml-1 rtl:mr-1 p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            aria-label={t('realtime.reconnect')}
          >
            <RefreshCw className="h-3 w-3" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div
        className={cn('flex items-center gap-2 text-sm', config.color, className)}
        role="status"
        aria-live="polite"
      >
        <Icon
          className={cn('h-4 w-4', status === 'connecting' && 'animate-spin')}
          aria-hidden="true"
        />
        <span>{t(`realtime.${status}`)}</span>
        {(status === 'disconnected' || status === 'error') && canRetry && onReconnect && (
          <button
            type="button"
            onClick={onReconnect}
            className={cn(
              'text-sm font-medium underline underline-offset-2',
              'hover:no-underline transition-all'
            )}
          >
            {t('realtime.reconnect')}
          </button>
        )}
      </div>
    );
  }

  // Banner variant
  if (status === 'connected') {
    return null; // Don't show banner when connected
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between px-4 py-3 rounded-lg',
        config.bgColor,
        className
      )}
      role="alert"
      aria-live="assertive"
    >
      <div className={cn('flex items-center gap-3', config.color)}>
        <Icon
          className={cn('h-5 w-5', status === 'connecting' && 'animate-spin')}
          aria-hidden="true"
        />
        <div>
          <p className="font-medium">{t(`realtime.${status}Title`)}</p>
          <p className="text-sm opacity-80">{t(`realtime.${status}Description`)}</p>
        </div>
      </div>

      {(status === 'disconnected' || status === 'error') && canRetry && onReconnect && (
        <button
          type="button"
          onClick={onReconnect}
          className={cn(
            'px-3 py-1.5 rounded-md text-sm font-medium',
            'bg-white dark:bg-secondary-800',
            'border border-current',
            'hover:bg-secondary-50 dark:hover:bg-secondary-700',
            'transition-colors',
            config.color
          )}
        >
          {t('realtime.tryAgain')}
        </button>
      )}
    </div>
  );
}

export const ConnectionStatus = memo(ConnectionStatusComponent);
export default ConnectionStatus;
